import { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

export default function AdminPassword() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('password');
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    new_email: user?.email || '',
    password: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    emailPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = (e) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleEmailChange = (e) => {
    setEmailForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setMessage({ type: '', text: '' });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'Bitte füllen Sie alle Felder aus' });
      return;
    }

    if (passwordForm.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Neues Passwort muss mindestens 6 Zeichen lang sein' });
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'Die Passwörter stimmen nicht überein' });
      return;
    }

    if (passwordForm.current_password === passwordForm.new_password) {
      setMessage({ type: 'error', text: 'Das neue Passwort muss sich vom aktuellen unterscheiden' });
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      setMessage({ type: 'success', text: 'Passwort erfolgreich geändert!' });
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!emailForm.new_email || !emailForm.password) {
      setMessage({ type: 'error', text: 'Bitte füllen Sie alle Felder aus' });
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.new_email)) {
      setMessage({ type: 'error', text: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' });
      return;
    }

    setLoading(true);
    try {
      await api.put('/auth/change-email', {
        new_email: emailForm.new_email,
        password: emailForm.password
      });
      
      setMessage({ type: 'success', text: 'E-Mail-Adresse erfolgreich geändert!' });
      setEmailForm(prev => ({ ...prev, password: '' }));
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Ändern der E-Mail-Adresse';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="max-w-xl" data-testid="admin-password-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-light mb-2">Konto-Einstellungen</h1>
        <p className="text-[#666666]">
          Verwalten Sie Ihr Admin-Konto
        </p>
      </div>

      {/* User Info */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#F9F9F7] rounded-full flex items-center justify-center">
            <Lock size={20} className="text-gold" />
          </div>
          <div>
            <p className="font-medium">{user?.username}</p>
            <p className="text-sm text-[#666666]">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setActiveTab('password'); setMessage({ type: '', text: '' }); }}
          className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'password'
              ? 'bg-[#0A0A0A] text-white'
              : 'bg-[#F9F9F7] text-[#666666] hover:bg-[#E5E5E5]'
          }`}
        >
          <Lock size={16} />
          Passwort ändern
        </button>
        <button
          onClick={() => { setActiveTab('email'); setMessage({ type: '', text: '' }); }}
          className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'email'
              ? 'bg-[#0A0A0A] text-white'
              : 'bg-[#F9F9F7] text-[#666666] hover:bg-[#E5E5E5]'
          }`}
        >
          <Mail size={16} />
          E-Mail ändern
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 mb-6 flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Password Form */}
      {activeTab === 'password' && (
        <form onSubmit={handlePasswordSubmit} className="bg-white border border-[#E5E5E5] p-6">
          <div className="space-y-6">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Aktuelles Passwort
              </label>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  name="current_password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                  placeholder="Ihr aktuelles Passwort"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Neues Passwort
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  name="new_password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                  placeholder="Mindestens 6 Zeichen"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordForm.new_password && passwordForm.new_password.length < 6 && (
                <p className="text-red-500 text-xs mt-1">Mindestens 6 Zeichen erforderlich</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Neues Passwort bestätigen
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  name="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                  placeholder="Passwort wiederholen"
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-red-500 text-xs mt-1">Passwörter stimmen nicht überein</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] text-white py-3 font-medium hover:bg-[#1A1A1A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="change-password-submit"
            >
              {loading ? (
                <div className="spinner w-5 h-5 border-white" />
              ) : (
                <>
                  <Lock size={18} />
                  Passwort ändern
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Email Form */}
      {activeTab === 'email' && (
        <form onSubmit={handleEmailSubmit} className="bg-white border border-[#E5E5E5] p-6">
          <div className="space-y-6">
            {/* New Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Neue E-Mail-Adresse
              </label>
              <input
                type="email"
                name="new_email"
                value={emailForm.new_email}
                onChange={handleEmailChange}
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
                placeholder="neue@email.de"
                data-testid="new-email"
              />
            </div>

            {/* Password Confirmation */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Passwort zur Bestätigung
              </label>
              <div className="relative">
                <input
                  type={showPasswords.emailPassword ? 'text' : 'password'}
                  name="password"
                  value={emailForm.password}
                  onChange={handleEmailChange}
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                  placeholder="Ihr aktuelles Passwort"
                  data-testid="email-password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('emailPassword')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                >
                  {showPasswords.emailPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] text-white py-3 font-medium hover:bg-[#1A1A1A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="change-email-submit"
            >
              {loading ? (
                <div className="spinner w-5 h-5 border-white" />
              ) : (
                <>
                  <Mail size={18} />
                  E-Mail ändern
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Security Tips */}
      <div className="mt-6 p-4 bg-[#F9F9F7] border border-[#E5E5E5]">
        <h3 className="font-medium mb-2">Sicherheitstipps</h3>
        <ul className="text-sm text-[#666666] space-y-1">
          <li>• Verwenden Sie mindestens 8 Zeichen für ein sicheres Passwort</li>
          <li>• Kombinieren Sie Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen</li>
          <li>• Verwenden Sie nicht dasselbe Passwort für mehrere Konten</li>
          <li>• Ändern Sie Ihr Passwort regelmäßig</li>
        </ul>
      </div>
    </div>
  );
}
